/**
 * GA4 + Consent Mode v2 helper.
 *
 * GA4 is loaded in index.html with all consent signals defaulted to "denied".
 * Nothing is sent to Google until the user accepts cookies through our banner,
 * which calls `updateConsent("granted")`. Choosing "necessary only" keeps
 * everything denied — gtag still loads but transmits no data, no client_id
 * cookie is written, and no pageviews / events fire.
 *
 * Use `trackEvent` for custom events (CTA clicks, form submissions, …). It is
 * always safe to call: if consent is denied or gtag has not loaded yet, the
 * event is silently dropped.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export const GA_MEASUREMENT_ID = "G-RT958LEEWH";

type ConsentState = "granted" | "denied";

/**
 * Granular consent categories surfaced in the cookie banner.
 *
 * - `necessary` is always granted — strictly required cookies (session,
 *   CSRF, language preference, the consent flag itself). Not controlled by
 *   Consent Mode and not user-toggleable.
 * - `analytics` controls Google Analytics (`analytics_storage`).
 * - `marketing` controls advertising signals (`ad_storage`,
 *   `ad_user_data`, `ad_personalization`). Currently no ad pixels are loaded,
 *   but we expose the toggle so the user makes an explicit decision and we
 *   stay forward-compatible if marketing tags are added later.
 */
export interface ConsentChoice {
  analytics: ConsentState;
  marketing: ConsentState;
}

export const CONSENT_DENIED_ALL: ConsentChoice = {
  analytics: "denied",
  marketing: "denied",
};

export const CONSENT_GRANTED_ALL: ConsentChoice = {
  analytics: "granted",
  marketing: "granted",
};

const safeGtag = (...args: unknown[]) => {
  if (typeof window === "undefined") return;
  // gtag is defined inline in index.html, but guard for safety.
  if (typeof window.gtag !== "function") return;
  window.gtag(...args);
};

/* ------------------------------------------------------------------ *
 * Active page language
 *
 * Set from `LanguageContext` whenever the locale changes. Auto-injected
 * into every `trackEvent` / `trackPageView` call as the `language` param,
 * so all GA4 reports can be split by `cs` vs `en` without each call site
 * having to pass it explicitly.
 *
 * Falls back to reading `<html lang>` (kept in sync by LanguageContext)
 * for any event fired before the first React render — e.g. the consent
 * grant pageview triggered from `updateConsent`.
 * ------------------------------------------------------------------ */

let currentLanguage: "cs" | "en" | undefined;

export const setAnalyticsLanguage = (lang: "cs" | "en") => {
  currentLanguage = lang;
};

const getLanguage = (): "cs" | "en" | undefined => {
  if (currentLanguage) return currentLanguage;
  if (typeof document !== "undefined") {
    const htmlLang = document.documentElement.lang;
    if (htmlLang === "en") return "en";
    if (htmlLang === "cs") return "cs";
  }
  return undefined;
};

/**
 * Update Google Consent Mode v2 signals from a granular consent choice.
 *
 * Call after the user makes a decision in the cookie banner. Each category
 * maps to its specific Consent Mode signals — analytics-only consent will
 * keep ad signals denied (and vice versa), so we never silently opt the user
 * into more than they accepted.
 *
 * Backwards-compatible shortcut: `updateConsent("granted" | "denied")`
 * applies the same state to ALL categories at once.
 */
export const updateConsent = (input: ConsentChoice | ConsentState) => {
  const choice: ConsentChoice =
    typeof input === "string"
      ? { analytics: input, marketing: input }
      : input;

  safeGtag("consent", "update", {
    analytics_storage: choice.analytics,
    ad_storage: choice.marketing,
    ad_user_data: choice.marketing,
    ad_personalization: choice.marketing,
  });

  // After the first analytics grant we want a pageview for the current
  // location, since the original `config` call in index.html ran while
  // analytics was denied and did not register one.
  if (choice.analytics === "granted" && typeof window !== "undefined") {
    const lang = getLanguage();
    safeGtag("event", "page_view", {
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
      ...(lang ? { language: lang } : {}),
    });
  }
};

/**
 * Track a custom GA4 event. No-op if gtag is unavailable or consent is denied
 * (Consent Mode handles the latter automatically — denied events are dropped
 * before being sent to Google).
 *
 * Auto-injects `language` (cs/en) from the active locale so every event can
 * be split by language in GA4 reports without each call site passing it.
 * Explicit `language` in `params` always wins.
 */
export const trackEvent = (
  name: string,
  params: Record<string, unknown> = {}
) => {
  const lang = getLanguage();
  const merged: Record<string, unknown> =
    lang && !("language" in params) ? { language: lang, ...params } : params;
  safeGtag("event", name, merged);
};

/**
 * Manually fire a page_view. Useful for SPA route changes — the default
 * `gtag('config', ...)` only fires once on initial load.
 */
export const trackPageView = (path: string, title?: string) => {
  if (typeof window === "undefined") return;
  const lang = getLanguage();
  safeGtag("event", "page_view", {
    page_location: window.location.origin + path,
    page_path: path,
    page_title: title ?? document.title,
    ...(lang ? { language: lang } : {}),
  });
};

/* ------------------------------------------------------------------ *
 * CTA event taxonomy
 *
 * One event name (`cta_click`) for ALL primary CTA buttons, with stable
 * `cta_id` values so reports stay consistent even if button labels change
 * or get translated. Keep IDs in `snake_case`, lowercase, ASCII only.
 * ------------------------------------------------------------------ */

export type CtaId =
  // Hero (homepage)
  | "hero_join_community"
  | "hero_more_about"
  // Subscribe popup (auto-triggered)
  | "popup_subscribe"
  // Community section newsletter form
  | "community_subscribe"
  // Sales / inquiry
  | "sales_inquiry_submit"
  | "sales_open_form"
  // Partners
  | "partner_visit"
  // Footer
  | "footer_cookie_settings"
  | "footer_social_facebook"
  | "footer_social_instagram"
  | "footer_social_youtube"
  // Header
  | "header_signin"
  | "header_locale_toggle"
  // Auth dialog / pages — surface where user *intent* to authenticate is expressed.
  // `auth_open_*` = user opened the auth flow (clicked a "Log in" / "Sign up" button).
  // `auth_switch_*` = user toggled between login/signup/forgot-password modes.
  // `auth_google_signin_click` = user clicked the Google OAuth button (no success
  //   confirmation possible client-side because the redirect leaves the SPA).
  // `signup_login_link` = on /signup page, user clicked "Already have an account? Log in".
  | "auth_open_login"
  | "auth_open_signup"
  | "auth_switch_to_login"
  | "auth_switch_to_signup"
  | "auth_switch_to_forgot"
  | "auth_google_signin_click"
  | "signup_login_link";

export interface CtaParams {
  /** Stable identifier (use a value from CtaId). */
  cta_id: CtaId;
  /** Where the CTA lives — section / page area. */
  cta_location: string;
  /** Visible label at click time (already localized). */
  cta_label?: string;
  /** Destination URL or route (if the CTA navigates somewhere). */
  cta_destination?: string;
  /**
   * Optional form association. If the CTA submits or opens a form, pass the
   * `form_name` so the click can be correlated with downstream `form_submit`
   * / `form_error` events. Auto-filled from `CTA_FORM_REGISTRY` when omitted.
   */
  form_name?: FormName;
  /** Any additional context (variant, position, etc.). */
  [key: string]: unknown;
}

/**
 * Stable mapping CTA → form. Used to auto-inject `form_name` into every
 * `cta_click` payload so the click and the resulting `form_submit` /
 * `form_error` events can be joined in GA4 explorations.
 *
 * Add an entry here whenever a new CTA opens or submits a form. CTAs that
 * just navigate (footer social, partner_visit, hero_more_about, …) stay out
 * of the registry — they have no form to correlate with.
 */
export const CTA_FORM_REGISTRY: Partial<Record<CtaId, FormName>> = {
  hero_join_community: "newsletter",
  popup_subscribe: "newsletter",
  community_subscribe: "newsletter",
  sales_inquiry_submit: "sales_inquiry",
  sales_open_form: "sales_inquiry",
  header_signin: "login",
  auth_open_login: "login",
  auth_open_signup: "signup",
  auth_switch_to_login: "login",
  auth_switch_to_signup: "signup",
  auth_switch_to_forgot: "password_reset_request",
  auth_google_signin_click: "login",
  signup_login_link: "login",
};

/**
 * Track a primary CTA click. Always sends event name `cta_click` so all CTAs
 * roll up into a single GA4 report; filter by `cta_id` for granular views.
 *
 * Auto-enriches the payload so EVERY click hit has a consistent shape:
 *   - `cta_id`, `cta_location` — required, passed by caller
 *   - `cta_label`, `cta_destination` — optional, passed by caller
 *   - `form_name` — auto-filled from `CTA_FORM_REGISTRY` when not provided
 *     (so CTAs that open/submit forms always carry the form association)
 *   - `language` — auto-injected by `trackEvent` from active locale
 *   - `page_path` — auto-filled from `window.location.pathname` so reports
 *     can split CTA performance by surface even when `cta_location` is
 *     intentionally generic (e.g. `footer`, `header_desktop`)
 *
 * Caller-supplied values always win over auto-fill (no silent overwrites).
 */
export const trackCta = (params: CtaParams) => {
  const enriched: Record<string, unknown> = { ...params };

  if (!enriched.form_name) {
    const mapped = CTA_FORM_REGISTRY[params.cta_id];
    if (mapped) enriched.form_name = mapped;
  }

  if (!("page_path" in enriched) && typeof window !== "undefined") {
    enriched.page_path = window.location.pathname;
  }

  trackEvent("cta_click", enriched);
};

/* ------------------------------------------------------------------ *
 * Form events
 *
 * Two events:
 *   - `form_submit`  → success (server confirmed)
 *   - `form_error`   → failure (validation, network, server, …)
 *
 * Both share the SAME `form_name` taxonomy and the SAME `outcome` parameter
 * (`'success' | 'error'`), so you can either:
 *   - Filter by event_name (`form_submit` vs `form_error`), or
 *   - Combine both into one report and split by `outcome` to compute
 *     conversion rates per form.
 *
 * Stable form_name values (do not rename — historical data lost):
 *   newsletter, sales_inquiry, login, signup, signup_dialog,
 *   password_reset_request, password_reset, lecture_booking,
 *   partnership_inquiry
 *
 * `error_type` taxonomy (use these literals when possible):
 *   validation       → client-side validation failed (bad input)
 *   auth             → wrong credentials / weak password / etc.
 *   network          → request never reached server (offline, CORS, …)
 *   server           → server returned an error (5xx, RPC failure)
 *   session_expired  → token/link expired
 *   unknown          → fallback
 * ------------------------------------------------------------------ */

export type FormName =
  | "newsletter"
  | "sales_inquiry"
  | "login"
  | "signup"
  | "signup_dialog"
  | "password_reset_request"
  | "password_reset"
  | "lecture_booking"
  | "partnership_inquiry";

export type FormErrorType =
  | "validation"
  | "auth"
  | "network"
  | "server"
  | "session_expired"
  | "unknown";

/**
 * Track a successful form submission (after server confirms success).
 * Always emits `outcome: 'success'` so it can be grouped alongside `form_error`.
 */
export const trackFormSubmit = (
  form_name: FormName,
  params: Record<string, unknown> = {}
) => {
  trackEvent("form_submit", {
    form_name,
    outcome: "success",
    ...params,
  });
};

/**
 * Track a failed form submission. Pair with every `trackFormSubmit` call.
 * Pass `error_type` from `FormErrorType` plus a short `error_message` (avoid
 * PII — never include user-entered email/name/etc. here).
 */
export const trackFormError = (
  form_name: FormName,
  error_type: FormErrorType,
  params: { error_message?: string } & Record<string, unknown> = {}
) => {
  trackEvent("form_error", {
    form_name,
    outcome: "error",
    error_type,
    ...params,
  });
};

/**
 * Conversion-grade event for newsletter signups. Fires AFTER successful
 * Ecomail enrollment. Mark this event as a "key event" in GA4 admin so it
 * shows up in conversion reports and audiences.
 */
export const trackNewsletterSubscribe = (
  source: "community_section" | "community_page" | "popup" | "sales_inquiry" | "subscription_dialog",
  params: Record<string, unknown> = {}
) => {
  trackEvent("newsletter_subscribe", { source, ...params });
};

/**
 * Authentication conversion event — fires AFTER a successful sign-in.
 * Companion to `trackFormSubmit('login')` but uses GA4's recommended event
 * name `login` so it shows up under the standard "Engagement → Events" report
 * and feeds the built-in user acquisition funnels. The `method` parameter is
 * also a GA4 recommended parameter (values like `email`, `google`, …).
 *
 * Usage:
 *   - Email/password login → call after `signIn()` returns no error.
 *   - Google OAuth → cannot be tracked client-side (redirect leaves the SPA
 *     before the success callback). Use `cta_click` with id
 *     `auth_google_signin_click` for the *intent* and rely on Supabase auth
 *     logs / GA4 user acquisition reports for the conversion side.
 *
 * Mark `login` as a Key Event in GA4 admin if you want it as a conversion.
 */
export type LoginMethod = "email" | "google";

export const trackLoginSuccess = (
  method: LoginMethod,
  params: Record<string, unknown> = {}
) => {
  trackEvent("login", { method, ...params });
};

/**
 * Companion to `trackLoginSuccess` — fires AFTER a successful sign-up.
 * Uses GA4's recommended event name `sign_up` so it surfaces under built-in
 * acquisition reports. Pair with `trackFormSubmit('signup' | 'signup_dialog')`.
 */
export const trackSignupSuccess = (
  method: LoginMethod,
  params: Record<string, unknown> = {}
) => {
  trackEvent("sign_up", { method, ...params });
};


/* ------------------------------------------------------------------ *
 * Blog reading engagement
 *
 * Three events for measuring how deeply users consume articles:
 *   - `article_view`       → fires once per article load (after consent)
 *   - `article_scroll`     → fires once per depth threshold (25/50/75/90 %)
 *   - `article_read`       → fires once when a user has actively spent
 *                            >= 30s AND >= 90s on the page (two milestones).
 *                            "Active" = tab visible, mouse/scroll/key activity
 *                            within the last 30s.
 *
 * Combine with `article_id`, `article_slug`, `article_category` and
 * `article_locale` for breakdown reports. Mark `article_read` (90s) as a
 * Key Event in GA4 if you want to use it as a soft conversion.
 * ------------------------------------------------------------------ */

export interface ArticleContext {
  article_id: string;
  article_slug: string;
  article_category?: string;
  article_locale: "cs" | "en";
  article_title?: string;
  /** Estimated reading time in minutes (from word count). */
  article_read_minutes?: number;
  /** Word count of full article body. */
  article_word_count?: number;
}

export const trackArticleView = (ctx: ArticleContext) => {
  trackEvent("article_view", { ...ctx });
};

export const trackArticleScroll = (
  ctx: ArticleContext,
  depth: 25 | 50 | 75 | 90
) => {
  trackEvent("article_scroll", { ...ctx, scroll_depth: depth });
};

export const trackArticleRead = (
  ctx: ArticleContext,
  /** Active reading time milestone in seconds. */
  seconds: 30 | 90
) => {
  trackEvent("article_read", { ...ctx, read_seconds: seconds });
};

/* ------------------------------------------------------------------ *
 * Server-side tracking via GA4 Measurement Protocol
 *
 * Some events can't be reliably captured client-side:
 *   - Adblockers (uBlock, Brave, Pi-hole) silently drop ~25-40% of gtag hits.
 *     For high-value conversions (newsletter, sales lead) that's lost data.
 *   - OAuth redirects unmount the SPA before the success callback runs, so
 *     a client-side `login`/`sign_up` event after Google sign-in never fires.
 *
 * For these cases we POST to our `ga4-track` edge function which forwards to
 * GA4 via the Measurement Protocol API. Server-to-server calls bypass
 * adblockers entirely and survive page unloads.
 *
 * Strategy: server-only for SELECTED conversions (no client-side duplicate).
 * Pageviews, scroll, CTA clicks etc. stay client-only — the gtag stream is
 * the source of truth for engagement, MP is only for conversion safety.
 * ------------------------------------------------------------------ */

/** Read GA4 client_id from the `_ga` cookie set by gtag.
 *  Format: `GA1.1.<random>.<timestamp>`. We pass the part AFTER `GA1.1.`
 *  (i.e. `<random>.<timestamp>`) to MP — that's what `gtag('get', ..., 'client_id')`
 *  would return. Returns undefined if cookie isn't there yet (e.g. first
 *  pageview before consent, or fresh session right after OAuth redirect). */
export const getGaClientId = (): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return undefined;
  // Strip the GA version prefix `GA1.1.` (or `GA1.2.`, etc).
  const raw = decodeURIComponent(match[1]);
  const parts = raw.split(".");
  if (parts.length >= 4) {
    // Last two parts are <random>.<timestamp>
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  }
  return raw;
};

/** Read current analytics consent state from localStorage. Mirrors the logic
 *  in index.html / CookieConsent.tsx — kept in sync so we don't accidentally
 *  send server hits for users who declined cookies. */
const hasAnalyticsConsent = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem("cookie-consent");
    if (!raw) return false;
    if (raw === "all") return true;
    if (raw === "necessary") return false;
    const parsed = JSON.parse(raw) as { analytics?: string };
    return parsed?.analytics === "granted";
  } catch {
    return false;
  }
};

interface ServerEventOptions {
  /** Optional Supabase user_id (UUID) — included as GA4 user_id when present. */
  userId?: string;
  /** Override the consent check. Use ONLY when you've already validated
   *  consent server-side and want to force-send (e.g. from auth listener
   *  where the user is signed-in and has implicitly accepted by signing up). */
  forceConsent?: boolean;
}

/**
 * Fire an event server-side via GA4 Measurement Protocol. Use for conversions
 * that adblockers would otherwise drop (newsletter, login, sign_up) or
 * events that happen across an OAuth redirect.
 *
 * Auto-includes:
 *   - `client_id` from `_ga` cookie (so server hit attaches to same user as
 *     client stream — critical for unified session reports)
 *   - `language` from active locale
 *   - `page_location`, `page_path` from current URL
 *
 * Silently no-ops if consent denied. Failures are logged but never thrown —
 * tracking must NEVER break a UX flow.
 */
export const trackEventServer = async (
  event: "newsletter_subscribe" | "login" | "sign_up" | "form_submit" | "form_error",
  params: Record<string, unknown> = {},
  options: ServerEventOptions = {}
): Promise<void> => {
  if (typeof window === "undefined") return;
  if (!options.forceConsent && !hasAnalyticsConsent()) return;

  const lang = getLanguage();
  const clientId = getGaClientId();

  const enrichedParams: Record<string, unknown> = {
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
    ...(lang ? { language: lang } : {}),
    ...params,
  };

  // Build URL pointing at the project's edge function. Use the published
  // anon-callable endpoint via the Supabase project URL so this works from
  // any deployed origin (preview, custom domain, …).
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (!supabaseUrl || !anonKey) return;

  const body = JSON.stringify({
    event,
    client_id: clientId,
    user_id: options.userId,
    consent: true,
    params: enrichedParams,
  });

  try {
    // Prefer sendBeacon when available — survives page unload (e.g. an OAuth
    // redirect happening immediately after the call). Falls back to fetch
    // with keepalive for browsers without beacon (Safari < 11, etc).
    const url = `${supabaseUrl}/functions/v1/ga4-track`;
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // sendBeacon can't set custom headers, so we fall back to fetch when
      // the function requires the apikey header (it does — Supabase gateway
      // rejects unauthenticated calls without it). Beacon stays as the
      // fallback for keepalive scenarios via fetch+keepalive instead.
    }
    await fetch(url, {
      method: "POST",
      mode: "cors",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body,
    });
  } catch (err) {
    // Tracking must never break the calling flow.
    if (typeof console !== "undefined") {
      console.warn("[analytics] server-side track failed:", err);
    }
  }
};



